/**
 * Task-manager board persistence adapter (sync Phase 7).
 *
 * Two backends behind one board-shaped API (list/read/write/delete):
 * - LEGACY: whole-board JSON files via the task_board_* Rust commands
 *   (AppLocalData/extensions/task-manager/boards/{id}.json) — device-local,
 *   unsynced. Used whenever the current workspace is not sync-enrolled.
 * - RECORDS: rows in extension_documents (board/group/task records; comments
 *   stay nested in their task — per-TASK LWW is the granularity win). Synced
 *   across devices/team via the normal engine. Used for sync-enrolled
 *   workspaces; on first use, legacy board files migrate one-time into
 *   records (originals kept on disk as backup — never deleted).
 *
 * Boards were historically app-global; the records backend scopes them to
 * the CURRENT workspace (that's what makes team boards shareable). The
 * legacy files remain the fallback for un-enrolled contexts.
 */
import { invoke } from '@tauri-apps/api/core';
import {
  LOCATION,
  deviceScopeId,
  isDeviceScope,
  locationOfScope,
  scopeForNewItem,
} from '../../../records/itemLocation';
import { deleteRecord, listRecords, syncRecordScope, upsertRecord } from '../../../records/recordsClient';
import { diffRecords, writesFor } from './boardChangeSet';
import { personalScopeId } from '../../../services/personalScope';
import { useTeamsStore } from '../../../store/teamsStore';
import { isViewerRole } from './boardRoles';
import { taskStats, labelDotColors } from './boardsOverview';
import { fromStoredTask, toStoredTask } from './taskDescriptionCodec';
import { aiMarkFields } from '../../../services/ai/aiAuthorship';

const EXTENSION_ID = 'task-manager';

/**
 * A board lives in one of two engine scopes:
 * - PRIVATE: `personal:<userId>` (signed in) / `personal:local` (signed out) —
 *   syncs across the user's own devices via the personal sync engine (LWW poll).
 * - TEAM: `team:<teamId>` — a shared scope that rides the realtime team channel
 *   (`team:{teamId}` broadcasts `sync_delta`), the same way team workspaces are
 *   realtime. Board records carry `visibility` + `teamId` so their scope is
 *   derivable at read/write time.
 */
export function teamScopeId(teamId) {
  return `team:${teamId}`;
}

/**
 * The engine scope a board belongs to, from its own visibility/teamId — and,
 * for a private board, where it lives (`location`, set when it was read; never
 * stored in the record, so moving a board changes no data).
 */
export function boardScopeKey(board) {
  if (board?.visibility === 'team' && board?.teamId != null) {
    return teamScopeId(board.teamId);
  }
  if (board?.location === LOCATION.DEVICE) {
    const device = deviceScopeId();
    if (device) return device;
  }
  return personalScopeId();
}

/**
 * Is the signed-in user a VIEWER on this team? The board tab's own read-only
 * gate asks the same question through `boardRoles.isViewerRole`, against the
 * same array; this wrapper exists so a caller without React (MCP's board write
 * handlers) gets the identical answer. A viewer's push is rejected by the
 * server and a rejected push jams that scope's outbox permanently, so any
 * writer outside the UI needs this mirror.
 */
export function isTeamViewer(teamId) {
  try {
    return isViewerRole(useTeamsStore.getState().teams || [], teamId);
  } catch {
    return false;
  }
}

/** All team scopes the current user can see boards in (one per team). */
function teamBoardScopes() {
  try {
    const teams = useTeamsStore.getState().teams || [];
    return teams
      .filter((t) => t?.id != null)
      .map((t) => ({ scopeKey: teamScopeId(t.id), teamId: String(t.id) }));
  } catch {
    return [];
  }
}

/**
 * Resolve the extension_documents scope for a board operation. Defaults to the
 * personal scope (private boards) when no explicit scope is given. Always
 * returns a scope (local-first via SQLite); the legacy per-board JSON files are
 * imported once by migrateLegacyBoards for the personal scope only.
 */
function recordsContext(scopeKey) {
  return { workspaceId: scopeKey || personalScopeId() };
}

// ── board <-> records ──────────────────────────────────────────────────────

export function decomposeBoard(board) {
  const scope = { scope_id: board.id };
  const records = [
    {
      id: board.id,
      record_type: 'board',
      parent_id: null,
      sort_key: null,
      data: {
        ...scope,
        name: board.name,
        description: board.description || '',
        color: board.color || null,
        viewMode: board.viewMode || null,
        // Board-level "Show completed tasks" toggle. Enumerated explicitly (like
        // the other board fields) so it survives a save→sync→reload round-trip;
        // normalized to a bool matching the app's `showDoneTasks !== false`
        // default (absent/true → true, only explicit false → false).
        showDoneTasks: board.showDoneTasks !== false,
        // "Progress counts in column headers" (Boards Redesign extras, §2.4):
        // the bar and "N open" under each list name. Same normalisation as
        // showDoneTasks — absent/true → true (today's always-on look), only an
        // explicit false hides it. A 1.0.6 client drops the key on save, which
        // falls back to true: harmless.
        showColumnProgress: board.showColumnProgress !== false,
        // The board's label palette: [{ id, name, color }]. Enumerated like
        // every other board field because this record's `data` is built
        // explicitly, not spread — a field left out here is a field that
        // silently does not survive a save/sync/reload. Tasks reference these
        // by id and ride the task spread, so they need no entry of their own.
        labels: Array.isArray(board.labels) ? board.labels : [],
        // Task keys (commit ↔ task links, taskKeys.js / taskNumbers.js): the
        // prefix of every task's key ("APB" in APB-42) and the highest number
        // handed out, so a deleted task's number is never reused. Enumerated
        // for the same reason as `labels`; null until the board is first
        // filled (useBoardTaskKeysFill). A task's own `number` rides its spread.
        keyPrefix: board.keyPrefix || null,
        taskSeq: Number.isInteger(board.taskSeq) && board.taskSeq > 0 ? board.taskSeq : null,
        // Old prefixes after a rename (taskKeyPrefixEdit.js): APB-42 keeps
        // resolving after the board became WEB. Absent → [] (never renamed).
        keyPrefixAliases: Array.isArray(board.keyPrefixAliases) ? board.keyPrefixAliases : [],
        // "Close tasks from commits" (commitAutoClose.js): on, and since when —
        // only commits made after it was switched on close anything. Absent →
        // off (boards made before slice 3 never close tasks on their own).
        closeFromCommits: board.closeFromCommits === true,
        closeFromCommitsSince: board.closeFromCommitsSince || null,
        // Ownership: 'team' boards live in the team scope + ride the realtime
        // team channel; anything else is a private (personal-scope) board.
        visibility: board.visibility === 'team' ? 'team' : 'private',
        teamId: board.visibility === 'team' && board.teamId != null ? String(board.teamId) : null,
        // Who created the board (Boards Redesign extras §2.8): the overview
        // tile's owner avatar on TEAM boards (a private board is always the
        // signed-in user's). Set on create, never edited; absent on boards
        // made before 1.0.7, which then show no owner.
        createdBy: board.createdBy != null ? String(board.createdBy) : null,
        createdAt: board.createdAt || null,
        updatedAt: board.updatedAt || null,
        // AI Act Art. 50(2): machine-readable mark on a board the in-app AI
        // assistant created. Enumerated for the same reason as everything
        // above (this `data` is built, not spread); only ever `true`, absent
        // otherwise, so boards made by people keep their exact shape. The
        // provenance keys (provider, model, MCP client) ride along the same
        // way, each only when present (services/ai/aiAuthorship.js).
        ...aiMarkFields(board),
      },
    },
  ];
  for (const group of board.groups || []) {
    records.push({
      id: group.id,
      record_type: 'group',
      parent_id: board.id,
      sort_key: null,
      data: { ...scope, ...group },
    });
  }
  for (const task of board.tasks || []) {
    records.push({
      id: task.id,
      record_type: 'task',
      parent_id: task.groupId || board.id,
      sort_key: null,
      // The description snapshot is stored compact (taskDescriptionCodec.js).
      data: { ...scope, ...toStoredTask(task) },
    });
  }
  return records;
}

/**
 * Wire `RemoteChange` (entity_type "extension_documents", as broadcast on the
 * `team:{teamId}` phoenix channel) -> the flat record shape assembleBoard/
 * patchBoardWithRecords expect. Mirrors the server-side unwrap in
 * apply_remote_change (src-tauri/src/sync/remote.rs): `data` carries
 * extension_id/record_type/parent_uid/sort_key alongside the record's own
 * fields; `id` and `deleted` live on the change itself. A delete's `data` may
 * be empty (record_type absent) — patchBoardWithRecords handles that by id.
 */
export function remoteChangeToRecord(change) {
  const { extension_id, record_type, parent_uid, sort_key, ...fields } = change?.data || {};
  return {
    id: change.entity_id,
    record_type,
    parent_id: parent_uid ?? null,
    sort_key,
    deleted: !!change.deleted,
    ...fields,
  };
}

/**
 * Deterministic order for a board's groups (lists) — position first, then id
 * as a tie-break. Two clients can independently create a group "at the end"
 * around the same time and land on the identical `position` value (see
 * handleAddListInline/moveTaskToNewList in TaskBoardTab.js, both purely
 * local: `position: prev.groups.length`). Without a stable secondary key, a
 * plain position sort leaves ties in array-insertion order, which differs
 * per client — a client's own locally-created group sits earlier in its
 * array than one it only received over the wire — so the same two lists can
 * render in a different left-to-right order on each machine. The drop target
 * itself always resolves by stable group id, never by visual position, so
 * this was never data corruption — just two machines disagreeing about which
 * column a given list was, which looked like a card landing in the wrong
 * list. Every group sort (here and in TaskBoardTab.js) must use this same
 * comparator so ties resolve identically everywhere.
 */
export function compareGroupsByPosition(a, b) {
  return (a.position ?? 0) - (b.position ?? 0) || (a.id < b.id ? -1 : a.id > b.id ? 1 : 0);
}

/**
 * Deterministic order for a group's tasks — same shape and rationale as
 * compareGroupsByPosition. Two clients adding a card to the same list around
 * the same time both compute `position: <local list length>` and land on the
 * identical value (handleAddTask in TaskBoardTab.js), and concurrent drag
 * reorders merged per-record by LWW can likewise leave duplicate positions.
 * A bare `a.position - b.position` leaves those ties in array-insertion
 * order, which differs per client, so the same two cards render in a
 * different order on each machine. Every task sort must use this comparator.
 */
export function compareTasksByPosition(a, b) {
  return (a.position ?? 0) - (b.position ?? 0) || (a.id < b.id ? -1 : a.id > b.id ? 1 : 0);
}

// Derived progress selectors (card 3.2). They live in boardProgress.js so the
// pure maths can be tested without the records/Tauri imports of this module.
export { groupProgress, boardProgress } from './boardProgress';

/**
 * Patch an already-assembled board in memory with incoming realtime records,
 * without a disk re-read — the WS-first fast path for team boards (engine
 * persistence + the disk-reconcile path still run in parallel as the
 * authoritative fallback; see useTeamBoardsRealtime + TaskBoardTab).
 *
 * Only group/task upserts and deletes apply here (drag, add/remove card,
 * rename list, edit task — the overwhelming majority of realtime edits).
 * Board-level record changes (rename board, recolor, etc.) are intentionally
 * left to the slower disk-reconcile path: they're rare, and a delete's `data`
 * often omits record_type, so board-vs-group-vs-task can't always be told
 * apart for a delete — upserts are gated on `scope_id` (this board's id) to
 * stay safe, and deletes are looked up by id in both arrays since ids are
 * globally unique. Returns `board` unchanged (same reference) if nothing in
 * `records` touched it, so callers can skip a repaint.
 */
// Content equality for the echo/no-op guard below. Key order can differ
// between a locally-assembled item and a wire one; a false negative just
// causes a redundant repaint, never wrong data.
function sameJson(a, b) {
  try {
    return JSON.stringify(a) === JSON.stringify(b);
  } catch {
    return false;
  }
}

/**
 * Union-merge a task's comments across a whole-record replace. A plain
 * last-array-wins (the incoming record simply overwrites `comments`) would
 * silently drop whichever side's comments array lost the race — a comment a
 * teammate posted concurrently just vanishes. Instead: every remote comment
 * is authoritative (its content wins for any id both sides have); a comment
 * only the LOCAL side has survives only if it's newer than the remote task's
 * updatedAt — i.e. the remote genuinely hasn't seen it yet (a concurrent
 * add) — otherwise the remote is assumed to have deleted it. Result sorted by
 * createdAt (oldest first), matching TaskDetailModal's own comment ordering.
 */
function mergeComments(localComments, remoteComments, remoteUpdatedAt) {
  const remote = remoteComments || [];
  const local = localComments || [];
  const remoteIds = new Set(remote.map((c) => c.id));
  const remoteTime = remoteUpdatedAt ? new Date(remoteUpdatedAt).getTime() : NaN;
  const localOnly = local.filter((c) => {
    if (remoteIds.has(c.id)) return false; // remote's copy wins, already included
    const createdAt = new Date(c.createdAt).getTime();
    if (Number.isNaN(createdAt)) return false;
    // No remote stamp to compare against — can't prove a delete, so keep it.
    return Number.isNaN(remoteTime) || createdAt > remoteTime;
  });
  return [...remote, ...localOnly].sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt));
}

/**
 * @param {object} [options]
 * @param {Set<string>} [options.skipIds] - entity ids (task/group ids, or the
 *   sentinel "__meta__" for a board-level record) to leave untouched — the
 *   caller's own unsaved local edits for those entities, so a live broadcast
 *   can't clobber them before the debounced persist flush lands. Everything
 *   else in `records` still applies at full entity granularity (this used to
 *   be an all-or-nothing skip of the whole board — see TaskBoardTab).
 */
export function patchBoardWithRecords(board, records, { skipIds } = {}) {
  if (!board || !records?.length) return board;
  let groups = board.groups || [];
  let tasks = board.tasks || [];
  let changed = false;

  for (const r of records) {
    if (skipIds && skipIds.has(r.id)) continue;
    if (r.deleted) {
      const gi = groups.findIndex((g) => g.id === r.id);
      if (gi !== -1) {
        if (!changed) { groups = [...groups]; changed = true; }
        groups.splice(gi, 1);
        continue;
      }
      const ti = tasks.findIndex((t) => t.id === r.id);
      if (ti !== -1) {
        if (!changed) { tasks = [...tasks]; changed = true; }
        tasks.splice(ti, 1);
      }
      continue;
    }
    // Upserts: only apply once we can confirm this record belongs to THIS
    // board (scope_id is always set for non-delete records by decomposeBoard).
    if (r.scope_id !== board.id) continue;
    // Board-level record: never applied here (left to the disk-reconcile
    // path — see below), but still respects the "__meta__" skip sentinel so
    // callers can reason about it the same way as any other entity id.
    if (r.record_type === 'board' && skipIds && skipIds.has('__meta__')) continue;
    // parent_id/sort_key are wire-envelope fields, stripped exactly like
    // assembleBoard does — leaving them on painted items would let them leak
    // back into `data` on the next decomposeBoard write.
    if (r.record_type === 'group') {
      const { deleted, record_type, scope_id, parent_id, sort_key, ...g } = r;
      const gi = groups.findIndex((x) => x.id === g.id);
      // Own echo / already-painted frame: identical content must not mint a
      // new board object (would repaint + re-sort for nothing).
      if (gi !== -1 && sameJson(groups[gi], g)) continue;
      if (!changed) { groups = [...groups]; changed = true; }
      if (gi !== -1) groups[gi] = g; else groups.push(g);
    } else if (r.record_type === 'task') {
      const { deleted, record_type, scope_id, parent_id, sort_key, ...stored } = r;
      const t = fromStoredTask(stored);
      // Same rule as assembleBoard: parent_id decides placement, data.groupId is
      // only a stale-able mirror of it.
      if (parent_id && parent_id !== board.id) t.groupId = parent_id;
      const ti = tasks.findIndex((x) => x.id === t.id);
      let next = t;
      if (ti !== -1) {
        const local = tasks[ti];
        // Stale-frame guard: a delayed/reordered broadcast must not paint an
        // older task state over a newer one the engine (and the sender's own
        // LWW) would keep — the UI would show data the reconcile path later
        // snaps back, i.e. a visible card jump. Frames without stamps fall
        // through to the equality check. Skipped entirely (no merge either).
        if (local.updatedAt && t.updatedAt && new Date(t.updatedAt) < new Date(local.updatedAt)) {
          continue;
        }
        // Both a local and remote version exist: merge comments instead of
        // letting the remote's array flatly replace the local one (see
        // mergeComments above).
        next = { ...t, comments: mergeComments(local.comments, t.comments, t.updatedAt) };
        if (sameJson(local, next)) continue;
      }
      if (!changed) { tasks = [...tasks]; changed = true; }
      if (ti !== -1) tasks[ti] = next; else tasks.push(next);
    }
    // record_type === 'board': left to the disk-reconcile path.
  }

  if (!changed) return board;
  groups.sort(compareGroupsByPosition);
  return { ...board, groups, tasks };
}

export function assembleBoard(records) {
  const board = records.find((r) => r.record_type === 'board');
  if (!board) return null;
  const strip = ({ record_type, parent_id, sort_key, scope_id, ...rest }) => rest;
  // `parent_id` is the authoritative placement: it is the column the store, the
  // server and the local re-key on account adoption all maintain. `data.groupId`
  // is only a mirror written by decomposeBoard, and it goes stale the moment ids
  // change — after a re-key the board would render with its groups but every
  // task unplaced. Fall back to the mirror when parent_id is absent (legacy rows
  // written before it was set).
  const placeTask = (r) => {
    const t = fromStoredTask(strip(r));
    if (r.parent_id && r.parent_id !== board.id) t.groupId = r.parent_id;
    return t;
  };
  return {
    ...strip(board),
    groups: records
      .filter((r) => r.record_type === 'group')
      .map(strip)
      .sort(compareGroupsByPosition),
    tasks: records.filter((r) => r.record_type === 'task').map(placeTask),
  };
}

// ── legacy file backend ────────────────────────────────────────────────────

async function legacyList() {
  const metas = (await invoke('task_board_list', { slug: EXTENSION_ID })) || [];
  return metas;
}

async function legacyRead(boardId) {
  const raw = await invoke('task_board_read', { slug: EXTENSION_ID, boardId });
  return raw ? JSON.parse(raw) : null;
}

// ── public API ─────────────────────────────────────────────────────────────

/** One-time per-workspace: pull legacy board files into records (files kept). */
async function migrateLegacyBoards(ctx) {
  const existing = await listRecords({
    workspaceId: ctx.workspaceId,
    extensionId: EXTENSION_ID,
    recordType: 'board',
    scopeId: null,
  });
  if (existing.length > 0) return;
  let metas = [];
  try {
    metas = await legacyList();
  } catch {
    return; // no legacy store (browser dev) — nothing to migrate
  }
  for (const meta of metas) {
    try {
      const board = await legacyRead(meta.id);
      if (!board) continue;
      await syncRecordScope({
        workspaceId: ctx.workspaceId,
        extensionId: EXTENSION_ID,
        scopeId: board.id,
        records: decomposeBoard(board),
      });
    } catch (err) {
      console.warn(`[task-manager] board ${meta.id} migration failed (legacy file kept):`, err);
    }
  }
  console.info(`[task-manager] migrated ${metas.length} legacy board(s) into synced records`);
}

export async function listBoardMetas() {
  const ctx = recordsContext();
  await migrateLegacyBoards(ctx);
  const boards = await listRecords({
    workspaceId: ctx.workspaceId,
    extensionId: EXTENSION_ID,
    recordType: 'board',
    scopeId: null,
  });
  return boards.map((b) => ({ id: b.id, name: b.name }));
}

const summarize = (b) => ({
  id: b.id,
  name: b.name,
  description: b.description || '',
  color: b.color || null,
  createdBy: b.createdBy != null ? String(b.createdBy) : null,
  // The board's task-key prefix: prefix uniqueness across boards, and the Git
  // page's task chips resolve a key's board by it (openTaskByKey.js).
  keyPrefix: b.keyPrefix || null,
  keyPrefixAliases: Array.isArray(b.keyPrefixAliases) ? b.keyPrefixAliases : [],
  taskCount: (b.tasks || []).length,
  createdAt: b.createdAt || null,
  updatedAt: b.updatedAt,
});

/** Grid summaries for one scope: two records_list calls (boards + tasks). */
async function scopeSummaries(scopeKey, meta) {
  const listArgs = { workspaceId: scopeKey, extensionId: EXTENSION_ID, scopeId: null };
  const [boards, tasks] = await Promise.all([
    listRecords({ ...listArgs, recordType: 'board' }),
    listRecords({ ...listArgs, recordType: 'task' }),
  ]);
  // Per board: the tasks, for the overview tile's done / overdue numbers
  // (cards 7.1/7.2) — the records are already here, so no extra call.
  const byBoard = new Map();
  for (const t of tasks) {
    const list = byBoard.get(t.scope_id);
    if (list) list.push(t);
    else byBoard.set(t.scope_id, [t]);
  }
  const now = new Date();
  return boards.map((b) => {
    const stats = taskStats(byBoard.get(b.id), now);
    return {
      ...summarize(b),
      taskCount: stats.total,
      doneCount: stats.done,
      overdueCount: stats.overdue,
      labelColors: labelDotColors(b),
      visibility: meta.visibility,
      teamId: meta.teamId,
      scopeKey,
      location: locationOfScope(scopeKey),
    };
  });
}

/**
 * Boards-grid summaries across every scope the user can see: the personal
 * scope (private boards) plus one `team:<teamId>` scope per team. Each summary
 * is tagged with { visibility, teamId, scopeKey } so the grid can badge team
 * boards and later reads/writes/deletes route to the right scope. Team scopes
 * that haven't synced yet simply return no rows (the Team page uses REST).
 */
export async function listBoardSummaries() {
  const personalScope = personalScopeId();
  await migrateLegacyBoards(recordsContext(personalScope));
  const device = deviceScopeId();
  const scopes = [
    { scopeKey: personalScope, visibility: 'private', teamId: null },
    // Free plan: boards kept on this device only (cloud/local items).
    ...(device ? [{ scopeKey: device, visibility: 'private', teamId: null }] : []),
    ...teamBoardScopes().map((s) => ({ ...s, visibility: 'team' })),
  ];
  const perScope = await Promise.all(
    scopes.map((s) =>
      scopeSummaries(s.scopeKey, s).catch((err) => {
        console.warn(`[task-manager] list summaries failed for ${s.scopeKey}:`, err);
        return [];
      })
    )
  );
  return perScope.flat();
}

/**
 * Plans v2 local caps: the user's OWN boards and their tasks, i.e. the private
 * ones (personal + this-device scopes). Team boards belong to the team and do
 * not count against anyone's plan.
 */
export async function countOwnBoardsAndTasks() {
  const own = (await listBoardSummaries()).filter((s) => s.visibility === 'private');
  return { boards: own.length, tasks: own.reduce((n, s) => n + (s.taskCount || 0), 0) };
}

async function readBoardInScope(boardId, scopeKey) {
  const records = await listRecords({
    workspaceId: scopeKey,
    extensionId: EXTENSION_ID,
    recordType: null,
    scopeId: String(boardId),
  });
  const board = assembleBoard(records);
  // Where it was found: writes route back there (boardScopeKey).
  return board ? { ...board, location: locationOfScope(scopeKey) } : null;
}

/**
 * Read one assembled board. When `scopeKey` is omitted the board is resolved
 * automatically: personal scope (+ legacy file) first, then every team scope —
 * so a board opened by id alone works whether it's private or team-owned. The
 * assembled board carries visibility/teamId, so later writes route correctly.
 */
export async function readBoard(boardId, scopeKey) {
  const primary = scopeKey || personalScopeId();
  const found = await readBoardInScope(boardId, primary);
  if (found) return found;
  if (primary === personalScopeId()) {
    const legacy = await legacyRead(boardId).catch(() => null);
    if (legacy) return legacy;
  }
  // Cross-scope search for boards opened by id alone (e.g. team boards).
  const others = [personalScopeId(), deviceScopeId(), ...teamBoardScopes().map((s) => s.scopeKey)].filter(
    (s) => s && s !== primary
  );
  for (const s of others) {
    const b = await readBoardInScope(boardId, s); // eslint-disable-line no-await-in-loop
    if (b) return b;
  }
  return null;
}

/**
 * Persist a board into its own scope (derived from visibility/teamId).
 *
 * With `{ changes }` (the open board's save, boardChangeLog.js) only what this
 * person changed is written — field by field, see writeBoardChanges; an empty
 * set writes nothing. Without it the whole board is written: a new board, an
 * import, or a caller that builds the board itself.
 */
export async function writeBoard(board, { changes } = {}) {
  if (changes) return writeBoardChanges(board, changes);
  const ctx = recordsContext(boardScopeKey(board));
  await syncRecordScope({
    workspaceId: ctx.workspaceId,
    extensionId: EXTENSION_ID,
    scopeId: board.id,
    records: decomposeBoard(board),
  });
}

/** What changed from board `prev` to board `next`, record by record (boardChangeSet.js). */
export function diffBoards(prev, next) {
  if (!prev || !next || prev === next) return new Map();
  return diffRecords(decomposeBoard(prev), decomposeBoard(next));
}

/**
 * Persist only what THIS person changed (`changes`, from diffBoards): created
 * records whole, changed records as the cached copy plus the changed fields,
 * deletes for what was deleted. Unlike writeBoard it never re-sends a
 * teammate's card from a stale screen and never tombstones a record this
 * screen has not seen yet — see boardChangeSet.js for the bug it closes.
 */
export async function writeBoardChanges(board, changes) {
  if (!changes || !changes.size) return 0;
  const ctx = recordsContext(boardScopeKey(board));
  const base = { workspaceId: ctx.workspaceId, extensionId: EXTENSION_ID };
  const cached = await listRecords({ ...base, scopeId: board.id });
  const { upserts, deletes } = writesFor(decomposeBoard(board), changes, new Map(cached.map((r) => [r.id, r])));
  for (const record of upserts) await upsertRecord({ ...base, record }); // eslint-disable-line no-await-in-loop
  for (const recordId of deletes) await deleteRecord({ ...base, recordId }); // eslint-disable-line no-await-in-loop
  return upserts.length + deletes.length;
}

/** Tombstone a board in its scope. `scopeKey` defaults to the personal scope. */
export async function deleteBoard(boardId, scopeKey) {
  const ctx = recordsContext(scopeKey);
  // Tombstone the whole scope (empty record set = everything tombstones).
  await syncRecordScope({
    workspaceId: ctx.workspaceId,
    extensionId: EXTENSION_ID,
    scopeId: String(boardId),
    records: [],
  });
}

// Sharing a private board with a team is NOT here: the server moves it in one
// request and the client follows (records/boardTeamMove.js, UI-only — a
// data-residency change is out for MCP at any grant level).

// Moving a board between the cloud and this device is NOT here. It is the
// user's action only, and this store is the MCP boards surface: see
// records/itemMove.js (moveBoardToDevice / moveBoardToCloud).

/** Re-exported so the UI can badge a board without importing itemLocation. */
export const LOCATION_DEVICE = LOCATION.DEVICE;

/**
 * Where a NEW private board goes (card C5): the cloud while the plan has room
 * for one there, this device once it does not. The user is told once, after
 * the board exists — see itemLocation.announceDeviceItem.
 */
export function locationForNewBoard() {
  return scopeForNewItem('boards') ? LOCATION.DEVICE : LOCATION.CLOUD;
}

/** Is this board kept on this device only? */
export function isDeviceBoard(board) {
  return board?.location === LOCATION.DEVICE || isDeviceScope(board?.scopeKey);
}

// "Make private" (team → personal) is not here either: records/boardTeamMove.js
// makeBoardPrivate, server-side like the share.
